/**
 * V13.21 (Itération E) — Reporting cross-éditions des Jeux du Pacifique
 *
 * Route : /reporting/jeux-du-pacifique
 *
 * Page publique de synthèse : évolution multi-éditions, top délégations,
 * disciplines phares Tahiti. Chaque item permet de naviguer vers les vues
 * détaillées (édition, délégation, discipline).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  BarChart3, Trophy, Flag, TrendingUp, Medal, Calendar, ArrowRight,
  Info, ChevronRight, Users, MapPin,
} from "lucide-react";

function formatDiscipline(slug) {
  if (!slug) return "—";
  return String(slug).split("-").filter(Boolean).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

const TYPE_LABEL = {
  pacific_games: "Jeux du Pacifique",
  pacific_mini_games: "Mini-Jeux du Pacifique",
};

function TinyMedalBar({ or: g = 0, argent = 0, bronze = 0, maxTotal = 1 }) {
  const total = g + argent + bronze;
  const p = (v) => (maxTotal > 0 ? Math.max(2, (v / maxTotal) * 100) : 0);
  if (!total) return <span className="text-xs italic text-muted-foreground">Aucune médaille</span>;
  return (
    <div className="flex items-center gap-0.5 h-2 w-full min-w-[80px] rounded overflow-hidden bg-slate-100 dark:bg-slate-800">
      {g > 0 && <div style={{ width: `${p(g)}%` }} className="h-full bg-[hsl(var(--aito-gold))]" title={`Or : ${g}`} />}
      {argent > 0 && <div style={{ width: `${p(argent)}%` }} className="h-full bg-slate-400" title={`Argent : ${argent}`} />}
      {bronze > 0 && <div style={{ width: `${p(bronze)}%` }} className="h-full bg-amber-700" title={`Bronze : ${bronze}`} />}
    </div>
  );
}

function MedalCounts({ or: g = 0, argent = 0, bronze = 0, compact = false }) {
  return (
    <span className={`inline-flex items-baseline gap-2 font-mono ${compact ? "text-xs" : "text-sm"}`}>
      <span className="text-[hsl(var(--aito-gold))]">🥇{g}</span>
      <span className="text-slate-500">🥈{argent}</span>
      <span className="text-amber-700">🥉{bronze}</span>
    </span>
  );
}

function TableSkeleton({ rows = 5 }) {
  return (
    <div className="space-y-2 p-3">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-8 w-full" />
      ))}
    </div>
  );
}

export default function GamesReportingPage() {
  const [type, setType] = useState("__all__");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("editions");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = type !== "__all__" ? { type } : {};
    api
      .get("/games/reporting/summary", { params })
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
      })
      .catch(() => {
        if (cancelled) return;
        setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [type]);

  const editions = data?.editions || [];
  const topDelegations = data?.top_delegations || [];
  const topDisciplines = data?.top_disciplines || [];

  // Max pour normaliser les barres
  const maxEditionTotal = Math.max(1, ...editions.map((e) => e.stats_pf?.total || 0));
  const maxDelegTotal = Math.max(1, ...topDelegations.map((d) => d.total || 0));

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-6" data-testid="games-reporting-page">
      {/* Hero */}
      <div className="rounded-2xl aito-grad-hero text-white p-5 sm:p-7 mb-5 overflow-hidden">
        <div className="flex items-start gap-4">
          <div className="rounded-full bg-white/15 border border-white/25 h-11 w-11 flex items-center justify-center shrink-0">
            <BarChart3 className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="font-display font-bold text-xl sm:text-2xl leading-tight">
              Reporting Jeux &amp; Mini-Jeux du Pacifique
            </h1>
            <p className="mt-1 text-sm text-white/85 max-w-2xl leading-relaxed">
              Vue transverse à toutes les éditions publiées :
              évolution des médailles de Tahiti, top délégations du Pacifique, disciplines phares.
            </p>
          </div>
        </div>
      </div>

      {/* Sélecteur type */}
      <div className="flex items-center gap-2 mb-4" data-testid="reporting-type-selector">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">Compétitions :</span>
        {[
          { v: "__all__", l: "Toutes" },
          { v: "pacific_games", l: "Jeux du Pacifique" },
          { v: "pacific_mini_games", l: "Mini-Jeux" },
        ].map((opt) => (
          <Button
            key={opt.v}
            variant={type === opt.v ? "default" : "outline"}
            size="sm"
            className={type === opt.v ? "bg-[hsl(var(--aito-lagoon))] text-white hover:bg-[hsl(var(--aito-lagoon-2))]" : ""}
            onClick={() => setType(opt.v)}
            data-testid={`reporting-type-${opt.v}`}
          >
            {opt.l}
          </Button>
        ))}
      </div>

      {loading ? (
        <Card><TableSkeleton rows={5} /></Card>
      ) : !data || editions.length === 0 ? (
        <Card className="p-10 text-center border-dashed" data-testid="reporting-empty">
          <BarChart3 className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
          <h3 className="font-display font-semibold text-lg mb-1">Pas encore de données à reporter</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Les statistiques apparaîtront après le commit des premiers imports d'éditions.
          </p>
          <Link to="/admin/games-import" className="inline-block mt-4">
            <Button variant="outline" size="sm">
              <Trophy className="h-4 w-4 mr-1.5" />Aller à l'import
            </Button>
          </Link>
        </Card>
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4" data-testid="reporting-tabs">
            <TabsTrigger value="editions" data-testid="reporting-tab-editions">Évolution par édition</TabsTrigger>
            <TabsTrigger value="delegations" data-testid="reporting-tab-delegations">Top délégations</TabsTrigger>
            <TabsTrigger value="disciplines" data-testid="reporting-tab-disciplines">Disciplines phares Tahiti</TabsTrigger>
          </TabsList>

          {/* Vue 1 : évolution par édition (timeline Tahiti) */}
          <TabsContent value="editions" className="space-y-3">
            <Card className="p-4" data-testid="reporting-editions">
              <div className="mb-3 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
                <h2 className="font-display font-semibold text-sm">Tahiti à travers les éditions</h2>
                <span className="ml-auto text-[10px] uppercase tracking-wide text-muted-foreground">
                  {editions.length} édition{editions.length > 1 ? "s" : ""} publiée{editions.length > 1 ? "s" : ""}
                </span>
              </div>
              <div className="space-y-2">
                {editions.map((e) => (
                  <div key={e.id} className="flex items-center gap-3 p-2 rounded border hover:border-[hsl(var(--aito-lagoon))]/40 hover:bg-slate-50 dark:hover:bg-slate-900/40" data-testid={`reporting-edition-${e.key}`}>
                    <div className="w-14 text-center shrink-0">
                      <div className="font-mono text-lg font-bold text-[hsl(var(--aito-lagoon))] leading-none">{e.year}</div>
                      <div className="text-[9px] text-muted-foreground uppercase mt-1">{e.type === "pacific_mini_games" ? "Mini-Jeux" : "Jeux"}</div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <Link to={`/memoire/jeux-du-pacifique/${e.key}`} className="font-semibold text-sm hover:text-[hsl(var(--aito-lagoon))] hover:underline">
                        {e.name}
                      </Link>
                      <div className="text-xs text-muted-foreground flex items-center gap-2">
                        <MapPin className="h-3 w-3" />
                        {[e.host_city, e.host_country].filter(Boolean).join(", ") || "—"}
                        <span className="opacity-50">·</span>
                        <span>{e.results_count.toLocaleString("fr-FR")} résultats</span>
                      </div>
                    </div>
                    <div className="w-24 shrink-0 hidden sm:block">
                      <TinyMedalBar or={e.stats_pf?.or} argent={e.stats_pf?.argent} bronze={e.stats_pf?.bronze} maxTotal={maxEditionTotal} />
                    </div>
                    <div className="shrink-0">
                      <MedalCounts compact or={e.stats_pf?.or} argent={e.stats_pf?.argent} bronze={e.stats_pf?.bronze} />
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* Vue 2 : top délégations cross-éditions */}
          <TabsContent value="delegations" className="space-y-3">
            <Card className="p-4" data-testid="reporting-delegations">
              <div className="mb-3 flex items-center gap-2">
                <Flag className="h-4 w-4 text-[hsl(var(--aito-gold))]" />
                <h2 className="font-display font-semibold text-sm">Palmarès des délégations</h2>
                <span className="ml-auto text-[10px] uppercase tracking-wide text-muted-foreground">
                  Cumul toutes éditions
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-900 border-b">
                    <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="px-3 py-2 w-10 text-center">#</th>
                      <th className="px-3 py-2">Délégation</th>
                      <th className="px-3 py-2 w-24 hidden sm:table-cell">Progression</th>
                      <th className="px-3 py-2">Médailles</th>
                      <th className="px-3 py-2 text-center">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topDelegations.map((d, i) => (
                      <tr key={d.delegation} className={`border-b last:border-0 hover:bg-slate-50 dark:hover:bg-slate-900/40 ${d.is_pf ? "bg-[hsl(var(--aito-gold))]/5 font-semibold" : ""}`} data-testid={`reporting-deleg-row-${i}`}>
                        <td className="px-3 py-2 text-center font-mono text-xs">{i + 1}</td>
                        <td className="px-3 py-2">
                          {d.is_pf && <Flag className="inline h-3 w-3 mr-1 text-[hsl(var(--aito-gold))]" />}
                          {d.delegation}
                        </td>
                        <td className="px-3 py-2 hidden sm:table-cell">
                          <TinyMedalBar or={d.or} argent={d.argent} bronze={d.bronze} maxTotal={maxDelegTotal} />
                        </td>
                        <td className="px-3 py-2"><MedalCounts compact or={d.or} argent={d.argent} bronze={d.bronze} /></td>
                        <td className="px-3 py-2 text-center font-mono font-bold">{d.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-[10px] italic text-muted-foreground flex items-center gap-1.5">
                <Info className="h-3 w-3" />
                Cliquer sur le nom d'une délégation (à venir dans une future itération) ouvrira sa timeline détaillée.
              </p>
            </Card>
          </TabsContent>

          {/* Vue 3 : disciplines phares Tahiti */}
          <TabsContent value="disciplines" className="space-y-3">
            <Card className="p-4" data-testid="reporting-disciplines">
              <div className="mb-3 flex items-center gap-2">
                <Medal className="h-4 w-4 text-[hsl(var(--aito-gold))]" />
                <h2 className="font-display font-semibold text-sm">Disciplines phares — Polynésie française</h2>
              </div>
              {topDisciplines.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">Aucune médaille polynésienne recensée pour le moment.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {topDisciplines.map((d) => (
                    <div key={d.discipline} className="p-3 rounded border bg-[hsl(var(--aito-gold))]/5 border-[hsl(var(--aito-gold))]/25" data-testid={`reporting-disc-${d.discipline}`}>
                      <div className="font-display font-semibold text-sm">{formatDiscipline(d.discipline)}</div>
                      <div className="mt-1"><MedalCounts or={d.or} argent={d.argent} bronze={d.bronze} /></div>
                      <div className="mt-1 text-[10px] text-muted-foreground font-mono">Total médailles : {d.total}</div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      )}

      <p className="mt-6 text-[11px] italic text-muted-foreground text-center flex items-center justify-center gap-1.5">
        <Info className="h-3 w-3" />
        Ces données sont calculées à partir des résultats officiels importés dans AITO.PF.
      </p>
    </div>
  );
}
